import { equals } from "angular";
import { Shape } from "konva/lib/Shape";
import { Rect } from "konva/lib/shapes/Rect";
import { Line } from "konva/lib/shapes/Line";
import { Text } from "konva/lib/shapes/Text";
import { Ellipse } from "konva/lib/shapes/Ellipse";
import { createTransformer, IHasFullTextAlignment, IPosition, WhiteboardShapeStateType } from "./index";
import { WhiteboardShape, IShapeState } from "./shape";
import { ISize, ISizeableState, IFontState, IHasBackgroundColor, IShapeReactionState, BasicShapeType } from "./util";
import { fireStateEvent } from "../events";
import { isDarkColor } from "../behaviors/shape-context-menu";
import { inlineEdit } from "../behaviors/inline-edit";
import { IBasicLinkState, IWhiteboardRendererContext } from "@gtmhub/whiteboards";
import { contentPadding } from "@gtmhub/whiteboards/rendering/shapes/note";
import { Transformer } from "konva/lib/shapes/Transformer";
import { KonvaEventObject } from "konva/lib/Core";
import { linkable, LinkingKonvaEventObject, allowLinkingTo, setLinkingEnabled, LINK_DROP, LINK_DRAGOVER, LINK_DRAGLEAVE } from "../behaviors/linkable";

export interface IBasicShapeState extends IShapeState, ISizeableState, IFontState, IShapeReactionState, IHasBackgroundColor, IHasFullTextAlignment {
  type: BasicShapeType;
  content: string;
}

const parallelogramWidthToHeightMinRatio = 0.8;

export class BasicShape extends WhiteboardShape<IBasicShapeState> {
  private contentNode: Text;
  private shape: Shape;
  private transformer: Transformer;
  static stateTypes: WhiteboardShapeStateType[] = ["rectangle", "ellipse", "rhombus", "rounded-rectangle", "parallelogram"];
  private pos: IPosition;
  private xDiff = 0;
  private yDiff = 0;

  constructor(state: IBasicShapeState, context: IWhiteboardRendererContext) {
    super(state, context);

    this.createShape();
    this.createContent(state);

    this.setContentPosition(state.size);
    this.resize(state.size);
    this.backgroundColor(state);

    if (state.type === "ellipse") {
      this.position({ x: state.position.x + state.size.width / 2, y: state.position.y + state.size.height / 2 });
    } else {
      this.position({ x: state.position.x, y: state.position.y });
    }

    linkable(this);
    allowLinkingTo(this);
    setLinkingEnabled(this, true);

    this.transformer = createTransformer(this.selectionOptions);
    this.attachTransformerBehaviour();
    this.attachShapeSelectionBehaviour();
    this.attachDragEvents();

    if (state.type === "parallelogram") {
      this.selectionOptions.widthToHeightMinRatio = parallelogramWidthToHeightMinRatio;
    }
  }

  getPosition(): { x: number; y: number } {
    return {
      x: this.state.type === "ellipse" ? this.x() - this.xDiff - this.state.size.width / 2 : this.x() - this.xDiff,
      y: this.state.type === "ellipse" ? this.y() - this.yDiff - this.state.size.height / 2 : this.y() - this.yDiff,
    };
  }
  updateState(state: IBasicShapeState): boolean {
    let updated = false;
    if (this.shouldUpdateState(state)) {
      this.createContent(state);
      updated = true;
    }

    const isSizeChanged = !equals(this.state.size, state.size);
    const isPositionChanged = !equals(this.state.position, state.position) && !equals(this.position(), state.position);

    if (isSizeChanged || isPositionChanged) {
      this.updateSizeAndPosition(state);
      updated = true;
    }

    if (this.state.backgroundColor !== state.backgroundColor) {
      this.backgroundColor(state);
      updated = true;
    }

    if (isPositionChanged) {
      if (state.type === "ellipse") {
        this.position({ x: state.position.x + state.size.width / 2, y: state.position.y + state.size.height / 2 });
      } else {
        this.position({ x: state.position.x, y: state.position.y });
      }
    }

    this.state = state;

    if (this.state.type === "rhombus" || this.state.type === "parallelogram") {
      this.shape.points(this.calculatePoints());
    }

    return updated;
  }

  private attachDragEvents() {
    this.on(`${LINK_DRAGOVER} ${LINK_DRAGLEAVE}`, (evt: LinkingKonvaEventObject) => {
      if (evt.shape === this) {
        return;
      }

      this.draw();
    });

    this.on(LINK_DROP, (evt: LinkingKonvaEventObject) => {
      if (evt.shape === this) {
        return;
      }

      const link: IBasicLinkState = {
        type: "connection",
        source: {
          type: (evt.shape as unknown as IBasicShapeState).type,
          id: evt.shape.state.id,
          orientation: "left",
        },
        target: {
          type: (this as unknown as IBasicShapeState).type,
          id: this.state.id,
          orientation: "left",
        },
      };

      fireStateEvent("linkChange", evt, evt.shape, { state: link });

      this.draw();
    });
  }

  private updateSizeAndPosition(state: IBasicShapeState): void {
    this.setContentPosition(state.size);
    this.resize(state.size);
  }

  private attachTransformerBehaviour(): void {
    this.pos = this.shape.position();

    this.transformer.on("transformstart", (evt) => this.fire("transformstart", evt));

    this.transformer.on("transformend", (e) => {
      this.transform(e, { notify: true });

      this.xDiff = this.pos.x - this.shape.position().x;
      this.yDiff = this.pos.y - this.shape.position().y;
    });
  }

  private attachShapeSelectionBehaviour(): void {
    this.on("shape-selected", () => {
      this.getLayer().add(this.transformer);
      this.transformer.forceUpdate();
      this.transformer.nodes([this.shape]).visible(true);
    });

    this.on("shape-unselected", () => {
      this.transformer.hide();
    });

    this.shape.on("transform", (e) => this.transform(e, { notify: false }));
  }

  private shouldUpdateState(newState: IBasicShapeState): boolean {
    return (
      this.state.content !== newState.content ||
      this.state.fontSize != newState.fontSize ||
      this.state.fontFamily !== newState.fontFamily ||
      this.state.bold !== newState.bold ||
      this.state.italic !== newState.italic ||
      this.state.strikeThrough !== newState.strikeThrough ||
      this.state.underline !== newState.underline ||
      this.state.textAlignment !== newState.textAlignment ||
      this.state.backgroundColor !== newState.backgroundColor
    );
  }

  private createShape() {
    switch (this.state.type) {
      case "rectangle":
        this.shape = new Rect();
        break;
      case "ellipse":
        this.shape = new Ellipse();
        break;
      case "rounded-rectangle":
        this.shape = new Rect({
          cornerRadius: 20,
        });
        break;
      case "rhombus":
      case "parallelogram": {
        this.shape = this.createLine();
        break;
      }
    }
    this.add(this.shape);
  }

  private createLine() {
    return new Line({
      points: this.calculatePoints(),
      closed: true,
    });
  }

  private calculatePoints(): number[] {
    switch (this.state.type) {
      case "rhombus": {
        return [
          this.state.size.width / 2,
          0,
          this.state.size.width,
          this.state.size.height / 2,
          this.state.size.width / 2,
          this.state.size.height,
          0,
          this.state.size.height / 2,
        ];
      }
      case "parallelogram": {
        //    a
        // |—————|_________________
        // |    /                 /
        // |   /                 /
        // |  /                 /
        // | / 60              /
        // |/_\_______________/
        // calculating 'a' so we can find the right coordinates of the parallelogram's dots
        const a = this.state.size.height * Math.tan((30 * Math.PI) / 180);

        return [a / 2, 0, this.state.size.width, 0, this.state.size.width - a / 2, this.state.size.height, 0, this.state.size.height];
      }
    }
  }

  private createContent(state: IBasicShapeState) {
    if (!this.contentNode) {
      this.contentNode = new Text({
        lineHeight: 1.4,
      });

      inlineEdit(this, this.contentNode, {
        multiline: true,
        autoGrow: true,
        placeholder: "",
        whiteboardBasicShapesEnabled: this.context.whiteboardBasicShapesEnabled,
        onUpdate: (evt, reason, value) => {
          if (reason === "outsideClick") {
            value = value.trim();

            if (value !== this.state.content) {
              fireStateEvent("stateChange", evt, this, { state: this.state, changes: { content: value } });
            }
          }
        },
      });

      this.add(this.contentNode);
    }

    this.contentNode.setAttrs({
      text: state.content || "",
      fontSize: state.fontSize,
      fontFamily: state.fontFamily,
      fontStyle: [state.bold ? "bold" : "", state.italic ? "italic" : ""].join(" ").trim(),
      textDecoration: [state.strikeThrough ? "line-through" : "", state.underline ? "underline" : ""].join(" ").trim(),
      align: state.textAlignment.textHorizontalAlignment,
      verticalAlign: state.textAlignment.textVerticalAlignment,
      fill: state.backgroundColor && isDarkColor(state.backgroundColor) ? "#ffffff" : "#384758",
      ellipsis: true,
    });
  }

  private backgroundColor(state: IBasicShapeState) {
    this.shape.fill(state.backgroundColor);
  }

  private transform(e: KonvaEventObject<unknown>, options: { notify: boolean }) {
    const size: ISize = {
      width: this.shape.scaleX() * this.shape.size().width,
      height: Math.abs(this.shape.scaleY()) * this.shape.size().height,
    };

    let position = this.transformer.position();

    this.xDiff = this.pos.x - this.shape.position().x;
    this.yDiff = this.pos.y - this.shape.position().y;

    this.state.position = position;

    this.updateSizeAndPosition(this.state);
    this.shape.scale({ x: 1, y: 1 });

    if (this.state.type === "parallelogram" || this.state.type === "rhombus") {
      this.shape.points(this.calculatePoints());
    }

    position = this.position();
    fireStateEvent("stateChange", e, this, { state: this.state, changes: { position, size }, notify: options.notify });
  }

  private resize(size: ISize) {
    this.shape.size({ width: size.width, height: size.height });
    this.contentNode.size(this.calculateContentNodeSize(size));
    this.size(size);
  }

  private calculateContentNodeSize(shapeSize: ISize): ISize {
    switch (this.state.type) {
      case "ellipse":
        return { width: Math.abs(this.shape.x() - this.contentNode.x()) * 2, height: Math.abs(this.shape.y() - this.contentNode.y()) * 2 };
      case "rhombus":
        return { width: shapeSize.width / 2, height: shapeSize.height / 2 };
      case "parallelogram":
        return { width: shapeSize.width - shapeSize.height * Math.tan((30 * Math.PI) / 180), height: shapeSize.height - contentPadding * 2 };
      default:
        return { width: shapeSize.width - contentPadding * 2, height: shapeSize.height - contentPadding * 2 };
    }
  }

  private setContentPosition(shapeSize: ISize) {
    const pos = this.calculateContentNodePosition(shapeSize);
    this.contentNode.x(pos.x);
    this.contentNode.y(pos.y);
  }

  private calculateContentNodePosition(shapeSize: ISize): IPosition {
    switch (this.state.type) {
      case "ellipse":
        return {
          x: this.shape.x() - shapeSize.width / 3,
          y: this.shape.y() - shapeSize.height / 3,
        };
      case "rhombus":
        return {
          x: this.shape.x() + shapeSize.width / 4,
          y: this.shape.y() + shapeSize.height / 4,
        };
      case "parallelogram":
        return {
          x: this.shape.x() + (this.state.size.height * Math.tan((30 * Math.PI) / 180)) / 2,
          y: this.shape.y() + contentPadding,
        };
      default:
        return {
          x: this.shape.x() + contentPadding,
          y: this.shape.y() + contentPadding,
        };
    }
  }
}
BasicShape.prototype.selectionOptions = { minSize: 100, resizable: true };
